/**
 * Modal para registrar un pago
 */

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Input, Select, Badge } from "@/components/ui";
import { DatePickerField } from "@/components/ui/DatePicker";
import { X, Loader2, CreditCard, User, DollarSign } from "lucide-react";
import { useCreatePayment } from "@/hooks/useFinance";
import { useAuthStore } from "@/stores/authStore";
import { Timestamp } from "firebase/firestore";
import { todayLocalDateString } from "@/lib/utils";
import type { Charge } from "@/types";

const paymentSchema = z.object({
  amount: z.number().min(1, "El monto debe ser mayor a 0"),
  paymentMethod: z.enum(
    ["efectivo", "transferencia", "nequi", "daviplata", "otro"],
    {
      required_error: "Selecciona un método de pago",
    },
  ),
  reference: z.string().optional(),
  paymentDate: z.string().min(1, "La fecha es requerida"),
});

type PaymentForm = z.infer<typeof paymentSchema>;

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  charge: Charge;
}

const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  partial: "bg-orange-100 text-orange-800",
  paid: "bg-green-100 text-green-800",
  overdue: "bg-red-100 text-red-800",
};

const statusLabels: Record<string, string> = {
  pending: "Pendiente",
  partial: "Pago Parcial",
  paid: "Pagado",
  overdue: "Vencido",
};

export default function PaymentModal({
  isOpen,
  onClose,
  charge,
}: PaymentModalProps) {
  const createMutation = useCreatePayment();
  const user = useAuthStore((state) => state.user);
  const profile = useAuthStore((state) => state.profile);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<PaymentForm>({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      amount: charge.balance,
      paymentDate: todayLocalDateString(),
      paymentMethod: "efectivo",
    },
  });

  const watchAmount = watch("amount");
  const watchMethod = watch("paymentMethod");

  const onSubmit = async (data: PaymentForm) => {
    if (!user) return;

    const paymentData = {
      chargeId: charge.id,
      clubId: charge.clubId,
      periodId: charge.periodId,
      memberId: charge.memberId,
      personId: charge.personId,
      personName: charge.personName,
      amount: data.amount,
      currency: "COP",
      paymentMethod: data.paymentMethod,
      reference: data.reference || "",
      paymentDate: Timestamp.fromDate(new Date(data.paymentDate + "T12:00:00")),
      status: "valid" as const,
      receivedBy: user.uid,
      receivedByName: profile?.displayName || user.displayName || "Sistema",
    };

    try {
      await createMutation.mutateAsync(paymentData);
      reset();
      onClose();
    } catch (error) {
      console.error("Error creating payment:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center shadow-md shadow-green-500/25">
              <CreditCard className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Registrar Pago
              </h2>
              <p className="text-sm text-gray-500">
                Cobro #{charge.id.slice(0, 8)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Info del cobro */}
        <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-gradient-to-br from-gray-300 to-gray-400 rounded-xl flex items-center justify-center shadow-md">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-medium text-gray-900">{charge.personName}</p>
              <p className="text-sm text-gray-500">{charge.conceptName}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 text-center">
            <div className="bg-white p-3 rounded-xl border-2 border-gray-100 shadow-sm">
              <p className="text-xs text-gray-500 mb-1">Total</p>
              <p className="font-bold text-gray-900">
                ${charge.amount.toLocaleString()}
              </p>
            </div>
            <div className="bg-white p-3 rounded-xl border-2 border-green-100 shadow-sm">
              <p className="text-xs text-gray-500 mb-1">Pagado</p>
              <p className="font-bold text-green-600">
                ${charge.amountPaid.toLocaleString()}
              </p>
            </div>
            <div className="bg-white p-3 rounded-xl border-2 border-orange-100 shadow-sm">
              <p className="text-xs text-gray-500 mb-1">Pendiente</p>
              <p className="font-bold text-orange-600">
                ${charge.balance.toLocaleString()}
              </p>
            </div>
          </div>

          <div className="mt-3 flex justify-center">
            <Badge className={statusColors[charge.status]}>
              {statusLabels[charge.status]}
            </Badge>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
          {/* Monto */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Monto a Pagar (COP) *
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="number"
                {...register("amount", { valueAsNumber: true })}
                max={charge.balance}
                className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-green-500/20 focus:border-green-500 transition-all duration-300"
                placeholder="0"
              />
            </div>
            {watchAmount > charge.balance && (
              <p className="text-sm text-orange-500 mt-1">
                El monto excede el balance pendiente
              </p>
            )}
            {errors.amount && (
              <p className="text-sm text-red-500 mt-1">
                {errors.amount.message}
              </p>
            )}

            {/* Botones de monto rápido */}
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={() => setValue("amount", charge.balance)}
                className="flex-1 py-1 px-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors duration-200"
              >
                Total: ${charge.balance.toLocaleString()}
              </button>
              {charge.balance > 50000 && (
                <button
                  type="button"
                  onClick={() =>
                    setValue("amount", Math.floor(charge.balance / 2))
                  }
                  className="flex-1 py-1 px-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors duration-200"
                >
                  Mitad: ${Math.floor(charge.balance / 2).toLocaleString()}
                </button>
              )}
            </div>
          </div>

          {/* Método de pago */}
          <Select
            label="Método de Pago *"
            {...register("paymentMethod")}
            error={errors.paymentMethod?.message}
          >
            <option value="efectivo">💵 Efectivo</option>
            <option value="transferencia">🏦 Transferencia Bancaria</option>
            <option value="nequi">📱 Nequi</option>
            <option value="daviplata">📱 Daviplata</option>
            <option value="otro">📝 Otro</option>
          </Select>

          {/* Referencia (para transferencias) */}
          {watchMethod !== "efectivo" && (
            <Input
              label="Número de Referencia"
              {...register("reference")}
              error={errors.reference?.message}
              placeholder="Ej: Código de transacción"
            />
          )}

          {/* Fecha de pago */}
          <DatePickerField
            control={control}
            name="paymentDate"
            label="Fecha de Pago *"
            error={errors.paymentDate?.message}
          />

          {/* Resumen */}
          {watchAmount > 0 && (
            <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl">
              <div className="flex justify-between items-center">
                <span className="text-green-800 font-medium">
                  Nuevo saldo después del pago:
                </span>
                <span className="font-bold text-green-900 text-lg">
                  ${Math.max(0, charge.balance - watchAmount).toLocaleString()}
                </span>
              </div>
              {charge.balance - watchAmount <= 0 && (
                <p className="text-sm text-green-600 mt-1">
                  ✓ El cobro quedará completamente pagado
                </p>
              )}
            </div>
          )}

          {/* Botones */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              className="rounded-xl"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={
                isSubmitting || createMutation.isPending || watchAmount <= 0
              }
              className="rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 shadow-lg shadow-green-500/25 hover:shadow-xl hover:shadow-green-500/30 transition-all duration-300"
            >
              {isSubmitting || createMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Procesando...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Registrar Pago
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
